import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Alert,
  IconButton,
  Tooltip,
  Paper,
  CircularProgress,
} from '@mui/material';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CheckIcon from '@mui/icons-material/Check';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useQuery } from '@tanstack/react-query';
import { LandingPageSettings, LeadMagnetSettings, landingPageApi } from '../../services/landingPageApi';
import {
  LandingPageHero,
  LandingPageAbout,
  LandingPageServices,
  LandingPageContact,
  LandingPageFooter,
} from '../public';

interface PreviewTabProps {
  landingPage: LandingPageSettings;
  leadMagnet: LeadMagnetSettings;
  slug: string;
  publicUrl: string;
  onPublishToggle: (isPublished: boolean) => void;
  isSaving: boolean;
}

export const PreviewTab = ({
  landingPage,
  publicUrl,
  onPublishToggle,
  isSaving,
}: PreviewTabProps) => {
  const [copied, setCopied] = useState(false);

  const isPublished = landingPage.isPublished || false;

  // Fetch preview data using authenticated API
  const {
    data: previewData,
    isLoading: isPreviewLoading,
    refetch: refetchPreview,
  } = useQuery({
    queryKey: ['landingPagePreview'],
    queryFn: landingPageApi.getPreview,
  });

  const handleCopyUrl = async () => {
    try {
      await navigator.clipboard.writeText(publicUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
    }
  };

  const handleOpenPreview = () => {
    // Open preview in new tab - use preview route if unpublished, public URL if published
    if (isPublished) {
      window.open(publicUrl, '_blank');
    } else {
      window.open('/preview/landing-page', '_blank');
    }
  };

  const handleRefreshPreview = useCallback(() => {
    refetchPreview();
  }, [refetchPreview]);

  return (
    <Box>
      <Typography variant="h6" sx={{ mb: 3 }}>
        Preview & Publish
      </Typography>

      {/* Publish Status */}
      <Paper
        sx={{
          p: 3,
          mb: 4,
          bgcolor: isPublished ? 'success.50' : 'grey.50',
          border: '1px solid',
          borderColor: isPublished ? 'success.200' : 'grey.200',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            {isPublished ? (
              <VisibilityIcon color="success" />
            ) : (
              <VisibilityOffIcon color="disabled" />
            )}
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {isPublished ? 'Published' : 'Not Published'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {isPublished
                  ? 'Your landing page is live and accessible to the public.'
                  : 'Your landing page is hidden from the public.'}
              </Typography>
            </Box>
          </Box>
          <Button
            variant={isPublished ? 'outlined' : 'contained'}
            color={isPublished ? 'inherit' : 'primary'}
            onClick={() => onPublishToggle(!isPublished)}
            disabled={isSaving}
            startIcon={isPublished ? <VisibilityOffIcon /> : <VisibilityIcon />}
          >
            {isSaving ? 'Saving...' : isPublished ? 'Unpublish' : 'Publish'}
          </Button>
        </Box>
      </Paper>

      {/* Public URL */}
      <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
        Your Public URL
      </Typography>

      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          p: 2,
          bgcolor: 'grey.100',
          borderRadius: 2,
          mb: 2,
        }}
      >
        <Typography
          variant="body1"
          sx={{
            fontFamily: 'monospace',
            flex: 1,
            overflow: 'hidden',
            textOverflow: 'ellipsis',
          }}
        >
          {publicUrl}
        </Typography>
        <Tooltip title={copied ? 'Copied!' : 'Copy URL'}>
          <IconButton onClick={handleCopyUrl} size="small">
            {copied ? <CheckIcon color="success" /> : <ContentCopyIcon />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Open in new tab">
          <IconButton onClick={handleOpenPreview} size="small">
            <OpenInNewIcon />
          </IconButton>
        </Tooltip>
      </Box>

      <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
        Share this URL with potential clients. They'll see your landing page and can submit their
        challenges to receive a free strategy plan.
      </Typography>

      {/* Preview Iframe */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="subtitle1" fontWeight={600}>
          Page Preview
        </Typography>
        <Tooltip title="Refresh preview">
          <IconButton onClick={handleRefreshPreview} size="small">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {!isPublished && (
        <Alert severity="info" sx={{ mb: 2 }}>
          Preview mode - Your page is not yet visible to the public. Click "Publish" above to make it
          live.
        </Alert>
      )}

      <Box
        sx={{
          border: '1px solid',
          borderColor: 'grey.300',
          borderRadius: 2,
          overflow: 'hidden',
          height: 600,
          position: 'relative',
        }}
      >
        {/* Browser Chrome */}
        <Box
          sx={{
            bgcolor: 'grey.200',
            px: 2,
            py: 1,
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            borderBottom: '1px solid',
            borderColor: 'grey.300',
          }}
        >
          <Box sx={{ display: 'flex', gap: 0.5 }}>
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FF5F56' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#FFBD2E' }} />
            <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: '#27C93F' }} />
          </Box>
          <Box
            sx={{
              flex: 1,
              bgcolor: 'white',
              borderRadius: 1,
              px: 2,
              py: 0.5,
              fontSize: 12,
              color: 'text.secondary',
              fontFamily: 'monospace',
            }}
          >
            {publicUrl}
          </Box>
        </Box>

        {/* Inline Preview Content */}
        <Box
          sx={{
            height: 'calc(100% - 40px)',
            overflow: 'auto',
            bgcolor: 'white',
          }}
        >
          {isPreviewLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
              }}
            >
              <CircularProgress />
            </Box>
          ) : previewData ? (
            <Box
              sx={{
                minHeight: '100%',
                display: 'flex',
                flexDirection: 'column',
                '--primary-color': previewData.landingPage.primaryColor || '#5D5FEF',
              }}
            >
              <LandingPageHero
                owner={previewData.owner}
                headline={previewData.landingPage.headline}
                subheadline={previewData.landingPage.subheadline}
                primaryColor={previewData.landingPage.primaryColor || '#5D5FEF'}
              />
              {previewData.landingPage.bio && (
                <LandingPageAbout
                  bio={previewData.landingPage.bio}
                  primaryColor={previewData.landingPage.primaryColor || '#5D5FEF'}
                />
              )}
              {previewData.landingPage.showServices &&
                previewData.services &&
                previewData.services.length > 0 && (
                  <LandingPageServices
                    services={previewData.services}
                    primaryColor={previewData.landingPage.primaryColor || '#5D5FEF'}
                  />
                )}
              <LandingPageContact
                email={previewData.landingPage.contactEmail}
                phone={previewData.landingPage.phone}
                website={previewData.landingPage.website}
                linkedIn={previewData.landingPage.linkedIn}
                instagram={previewData.landingPage.instagram}
                primaryColor={previewData.landingPage.primaryColor || '#5D5FEF'}
              />
              <Box sx={{ flexGrow: 1 }} />
              <LandingPageFooter primaryColor={previewData.landingPage.primaryColor || '#5D5FEF'} />
            </Box>
          ) : (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                height: '100%',
                color: 'text.secondary',
              }}
            >
              <Typography>Unable to load preview</Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Quick Tips */}
      <Box sx={{ mt: 4 }}>
        <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>
          Tips for a Great Landing Page
        </Typography>
        <Box component="ul" sx={{ pl: 2, color: 'text.secondary' }}>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Write a compelling headline that speaks to your ideal client's pain points.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Keep your bio concise but personal - let prospects know who you are.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Add your services to showcase what you offer.
            </Typography>
          </li>
          <li>
            <Typography variant="body2" sx={{ mb: 1 }}>
              Customize the lead magnet message to match your brand voice.
            </Typography>
          </li>
          <li>
            <Typography variant="body2">
              Share your URL on social media and in your email signature.
            </Typography>
          </li>
        </Box>
      </Box>
    </Box>
  );
};
